import { api } from './api';

// ============================================================
// Shared site-wide content. Public endpoints — no auth.
// Used by footers, help center, contact-us, static CMS pages.
// ============================================================

export type SocialMedia = {
  id: string;
  facebook_url: string | null;
  twitter_url: string | null;
  linkedin_url: string | null;
  instagram_url: string | null;
  youtube_url: string | null;
};

// ============================================================
// Admin-managed landing-page images (Site Images). Public read.
// The website maps slug → presigned URL and falls back to its
// bundled default asset for any slot the admin hasn't set yet.
// ============================================================

export type SiteImageRow = {
  slug: string;
  alt_text: string | null;
  image_url_preview_url: string | null;
};

export async function getSiteImages() {
  const res = await api.get('/public/site-images');
  return res.data as { status: boolean; data: SiteImageRow[] };
}

export type AppSupport = {
  id: string;
  email: string;
  mobile_dial_code: string | null;
  mobile_number: string | null;
  whatsapp_dial_code: string | null;
  whatsapp_mobile_number: string | null;
};

export type Faq = {
  id: string;
  question: string;
  answer: string;
  category: string;
  icon: string | null;
  sort_order: number;
  is_featured: boolean;
  user_type: 'CUSTOMER' | 'PROFESSIONAL';
  created_at: string;
  updated_at: string;
};

export async function getSocialMedia() {
  const res = await api.get('/social-media/public');
  return res.data as { status: boolean; info: SocialMedia | null };
}

export async function getAppSupport() {
  const res = await api.get('/app-support/public');
  return res.data as { status: boolean; info: AppSupport | null };
}

export async function getFaqs(
  userType: 'CUSTOMER' | 'PROFESSIONAL',
  category?: string,
) {
  const res = await api.get('/faq/public', {
    params: { user_type: userType, ...(category ? { category } : {}) },
  });
  return res.data as { status: boolean; data: Faq[]; total_count: number };
}

// Long-form help center articles. Audience filters which portal the
// article was authored for — CUSTOMER pages should pass 'CUSTOMER';
// the backend includes BOTH-audience articles automatically.
export type HelpArticle = {
  id: string;
  code: string;
  title: string;
  summary: string | null;
  body: string;
  audience: 'CUSTOMER' | 'PROFESSIONAL' | 'BOTH';
  icon: string | null;
  cover_image_url: string | null;
  is_featured: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export async function getHelpArticles(
  audience: 'CUSTOMER' | 'PROFESSIONAL',
  opts?: { featured?: boolean; limit?: number },
) {
  const res = await api.get('/help-article/public', {
    params: {
      audience,
      ...(opts?.featured ? { featured: 'true' } : {}),
      ...(opts?.limit ? { limit: opts.limit } : {}),
    },
  });
  return res.data as { status: boolean; data: HelpArticle[] };
}

export async function getHelpArticle(
  code: string,
  audience: 'CUSTOMER' | 'PROFESSIONAL',
) {
  const res = await api.get(
    `/help-article/public/${encodeURIComponent(code)}`,
    { params: { audience } },
  );
  return res.data as { status: boolean; info: HelpArticle | null };
}

export type ServiceCategory = {
  id: string;
  name: string;
  code: string;
  description: string | null;
  icon: string | null;
  image_url: string | null;
  booking_flow: 'SESSION' | 'TREATMENT';
  sort_order: number;
  status: 'ACTIVE' | 'INACTIVE';
};

export async function getServiceCategories() {
  const res = await api.get('/service-category/public');
  return res.data as { status: boolean; data: ServiceCategory[]; total_count: number };
}

// Public services with the dynamic "Starting from £X/hr" price the
// backend computes from MIN active pro rate × (1 + platform fee).
// Used to drive the landing page service cards and the cleaning
// category card so the price isn't hardcoded.
export type PublicService = {
  id: string;
  name: string;
  code: string;
  description: string | null;
  image: string | null;
  // Short-lived presigned URL for `image` (private bucket). Prefer it
  // over the raw `image` when rendering.
  image_preview_url?: string | null;
  pricing_unit: 'PER_HOUR' | 'PER_BOOKING';
  base_price: number;
  currency: string;
  duration_minutes: number;
  badge: string | null;
  sort_order: number;
  status: 'ACTIVE' | 'INACTIVE';
  // Admin "show in Popular at-home services" flag (max 3 across the
  // catalogue). Drives the landing band.
  is_popular: boolean;
  starting_from_price: number;
  category: {
    id: string;
    name: string;
    code: string;
  };
};

export async function getPublicServices(opts?: { serviceCategoryId?: string }) {
  // Use the public-catalogue endpoint (/public/services). The older
  // /service/public route returns the same data but currently errors on
  // prod (a wrong column name in its query); this one is the resilient
  // source the rest of the catalogue already uses.
  const res = await api.get('/public/services', {
    params: opts?.serviceCategoryId
      ? { category_id: opts.serviceCategoryId }
      : {},
  });
  const data = ((res.data?.data ?? []) as any[]).map((s) => ({
    ...s,
    // The landing band + "Starting from £X/hr" lines read
    // `starting_from_price`; this endpoint exposes the same cheapest-
    // inclusive figure as `from_price`.
    starting_from_price:
      s.starting_from_price ?? s.from_price ?? s.display_price ?? 0,
    // `popular_image` here is a RAW (unsigned) S3 URL that 403s from the
    // private bucket on the public site — null it so the popular band
    // falls back to the SIGNED `image_preview_url`.
    popular_image: null,
  }));
  return {
    status: (res.data?.status ?? true) as boolean,
    data: data as PublicService[],
    total_count: (res.data?.total_count ?? data.length) as number,
  };
}

// Format a starting-from line for a card. Keeps the wording rule in
// one place — "Starting from £X/hr" (or "£X" for PER_BOOKING).
export function formatStartingFromLine(svc: PublicService): string {
  const amount = svc.starting_from_price;
  const display = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
  const suffix = svc.pricing_unit === 'PER_HOUR' ? '/hr' : '';
  return `Starting from £${display}${suffix}`;
}

// Admin-curated reviews surfaced on the landing pages. The admin stars
// individual reviews in the Reviews moderation screen; only visible
// (non-hidden) featured reviews that carry a written comment come back.
export type FeaturedReview = {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  customer: { first_name: string | null; last_name: string | null } | null;
  professional: { business_name: string | null } | null;
  service: { id: string; name: string } | null;
};

export async function getFeaturedReviews(limit = 12) {
  const res = await api.get('/public/reviews/featured', {
    params: { limit },
  });
  return res.data as { status: boolean; data: FeaturedReview[] };
}

export async function submitContactMessage(payload: {
  name: string;
  email?: string;
  message: string;
  dial_code?: string;
  mobile_number?: string;
}) {
  const res = await api.post('/contact-message', payload);
  return res.data as { status: boolean; message: string };
}
